import { useEffect, useState } from 'react';

import { Drawer, PeachjarLoader } from '@/components';
import { useAnalytics } from '@/hooks';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import { Tag } from '@/design-system';
import { AidaOffNotification } from './AidaOffNotification';
import { FlyerDistrict } from '@/graphql/types';
import { AidaStatus } from '@/config/constants';
import { TranslateFlyerResponse } from '@/graphql/types';
import { TRANSLATE_FLYER_MUTATION } from '@/graphql/mutation';
import { useMutation } from '@apollo/client';

import styles from './AccessibleVersion.styles';
import { AidaHeader } from './AidaHeader';
import { AccessibleDrawerHeader } from './AccessibleDrawerHeader';

interface Props {
  wcagHtml?: string;
  isOpen: boolean;
  onToggle: (state: boolean) => void;
  preSelectedLanguage?: string;
  district?: FlyerDistrict;
  id: number;
}

export const AccessibleVersion: React.FC<Props> = ({
  wcagHtml,
  isOpen,
  onToggle,
  preSelectedLanguage = '',
  district,
  id,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [textVersion, setTextVersion] = useState<string>('');
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null);
  const { trackEvent } = useAnalytics();
  const [translateFlyer] = useMutation<TranslateFlyerResponse>(TRANSLATE_FLYER_MUTATION);

  const { aidaExpirationDate, aidaStatus } = district?.communitySettings || {};

  const getTrialDaysLeft = (expirationDate?: string | null): number | null => {
    if (!expirationDate) return null;
    const expiration = new Date(expirationDate);
    const today = new Date();
    const diffTime = expiration.getTime() - today.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => {
    if (isOpen) {
      if (preSelectedLanguage) {
        onSelectLanguage(preSelectedLanguage);
      } else {
        setTextVersion(wcagHtml || '');
      }
      if (aidaExpirationDate) {
        setTrialDaysLeft(getTrialDaysLeft(aidaExpirationDate?.toString()));
      }
    } else {
      setSelectedLanguage('');
    }
  }, [isOpen, wcagHtml, preSelectedLanguage, aidaExpirationDate]);

  const onSelectLanguage = async (newLanguage: string) => {
    if (!wcagHtml || (aidaStatus === AidaStatus.off)) return;
    setLoading(true);
    setSelectedLanguage(newLanguage);
  
    try {
      const { data } = await translateFlyer({
        variables: {
          input: {
            flyerId: id,
            language: newLanguage
          }
        }
      })
      const translatedText = data?.translateFlyer?.translatedWcag || ''
      setTextVersion(translatedText);
    } catch (error) {
      console.log('error: ', error)
      setTextVersion(wcagHtml)
      setLoading(false)
    }
    trackEvent({
      event: TrackingEvents.translate,
      event_detail: EventDetails.translateFlyerText,
      stage: TrackingStage.discovery,
    });
    setLoading(false);
  };

  return (
    <Drawer
      title={<AidaHeader />}
      titleId="accessible-flyer-dialog-title"
      isOpen={isOpen}
      onToggle={onToggle}
      full
      customHeader={({ title, titleId, onClose }) => (
        <AccessibleDrawerHeader
          title={title}
          titleId={titleId}
          onClose={onClose}
          selectedLanguage={selectedLanguage}
          onSelectLanguage={onSelectLanguage}
          disabled={aidaStatus === AidaStatus.off}
        />
      )}
    >
      {isOpen && (
        <div className={styles.container}>
          {loading && <PeachjarLoader loading />}
          {aidaStatus === AidaStatus.trial && (
            <Tag className={styles.tag} variant="beta">
              Trial ends in {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'}
            </Tag>
          )}
          {aidaStatus === AidaStatus.off && <AidaOffNotification />}
          {wcagHtml && !loading && (
            <div
              className={`accessible-content ${
                aidaStatus === AidaStatus.off && styles.blurredContent
              }`}
              {...(selectedLanguage ? { lang: selectedLanguage } : {})}
             dangerouslySetInnerHTML={{ __html: textVersion }}
            />
          )}
        </div>
      )}
    </Drawer>
  );
};
